<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from '../i18n/t'
import { useAuthStore } from '../stores/auth'
import { useWalletStore } from '../stores/wallet'
import { api, ApiError } from '../api/http'
import { readClientDeviceAsync } from '../account/device'

type FeedbackKind = 'suggestion' | 'complaint' | 'bug'

const KINDS: FeedbackKind[] = ['suggestion', 'complaint', 'bug']

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const wallet = useWalletStore()
const kind = ref<FeedbackKind>('suggestion')

watch(
  () => route.query.report,
  (report) => {
    if (report === 'bug') kind.value = 'bug'
  },
  { immediate: true },
)
const message = ref('')
const busy = ref(false)
const status = ref<'ok' | 'bug' | 'error' | 'rate' | 'db' | ''>('')
const canSend = computed(
  () => message.value.trim().length >= 12 && !busy.value,
)

async function submit(): Promise<void> {
  status.value = ''
  busy.value = true
  try {
    const sentKind = kind.value
    const body = await api<{ coinGranted?: boolean; coins?: number }>('/feedback', {
      method: 'POST',
      token: auth.token,
      body: JSON.stringify({
        kind: sentKind,
        message: message.value,
        device: await readClientDeviceAsync(),
      }),
    })
    if (typeof body.coins === 'number') wallet.setCoins(body.coins)
    message.value = ''
    status.value = sentKind === 'bug' ? 'bug' : 'ok'
  } catch (err) {
    if (err instanceof ApiError && err.code === 'rate_limited') {
      status.value = 'rate'
    } else if (err instanceof ApiError && err.code === 'database_unavailable') {
      status.value = 'db'
    } else {
      status.value = 'error'
    }
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <form class="feedback" @submit.prevent="submit">
    <fieldset class="scheme-switch wrap">
      <legend class="visually-hidden">{{ t('feedback.kind') }}</legend>
      <button
        v-for="id in KINDS"
        :key="id"
        type="button"
        class="scheme-btn"
        :class="{ active: kind === id }"
        :aria-pressed="kind === id"
        @click="kind = id"
      >
        {{ t(`feedback.kinds.${id}`) }}
      </button>
    </fieldset>
    <label class="field">
      <span>{{ t('feedback.message') }}</span>
      <textarea
        id="feedback-message"
        v-model="message"
        rows="5"
        maxlength="2000"
        minlength="12"
        :placeholder="t('feedback.placeholder')"
        required
      />
    </label>
    <p v-if="status === 'bug'" class="note ok">{{ t('feedback.thanksBug') }}</p>
    <p v-else-if="status === 'ok'" class="note ok">{{ t('feedback.thanks') }}</p>
    <p v-else-if="status === 'rate'" class="note err">{{ t('feedback.rate') }}</p>
    <p v-else-if="status === 'db'" class="note err">
      {{ t('auth.errors.database_unavailable') }}
    </p>
    <p v-else-if="status === 'error'" class="note err">{{ t('feedback.error') }}</p>
    <button type="submit" class="save-btn" :disabled="!canSend">
      {{ t('feedback.send') }}
    </button>
  </form>
</template>

<style scoped>
.feedback {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scheme-switch {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  width: fit-content;
  margin: 0;
  padding: 4px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 20px;
}

.scheme-btn {
  min-height: 40px;
  padding: 0 14px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.scheme-btn.active {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface-variant);
}

textarea {
  min-height: 120px;
  padding: 12px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 10px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  resize: vertical;
}

.save-btn {
  align-self: flex-start;
  min-height: 40px;
  padding: 0 20px;
  border: 0;
  border-radius: 20px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.note {
  margin: 0;
  font-size: 13px;
}

.ok {
  color: var(--md-sys-color-primary);
}

.err {
  color: #e07070;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
</style>
